'use client';

import {
  CrmTableIcon,
  KitchenDetailsIcon,
  LocationIcon,
} from '@/_assets/icon-components';
import Home2Icon from '@/_assets/icon-components/Home2';
import NumberToPrice from '@/_components/NumberToPrice';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import { ISingleOrder } from '@/_service/types/orders';
import numToPrice from '@/_utils/numToPrice';
import { Box, Flex, HStack, Separator, Stack, Text } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';

const Desktop = ({
  order,
  total,
  change,
}: {
  order: ISingleOrder;
  total: number;
  change: number;
}) => {
  const t = useTranslations();
  const currentLocale = useCustomLocale();

  return (
    <Box px={6}>
      <Text py={2} fontSize={20} fontWeight={700}>
        {t('delivery_and_payment')}
      </Text>
      <Stack gap={2}>
        <Box>
          {order?.delivery_type === 'self-pickup' ||
          order?.delivery_type === 'hall' ? (
            <Flex gap={4} py={2} color={'text'} alignItems={'center'}>
              <LocationIcon color='#676767' />
              <Box>
                <Text color={'gray.600'} fontSize={'sm'}>
                  {t('branch')}
                </Text>
                <Text fontWeight={500}>{order?.steps[0]?.branch_name}</Text>
              </Box>
            </Flex>
          ) : (
            <>
              <Flex gap={4} py={2} alignItems={'center'}>
                <KitchenDetailsIcon color='#676767' />
                <Box>
                  <Text color={'gray.600'} fontSize={'sm'}>
                    {t('where_from')}
                  </Text>
                  <Text fontWeight={500}>{order?.steps[0]?.branch_name}</Text>
                </Box>
              </Flex>
              <Flex gap={4} py={3} alignItems={'center'}>
                <Home2Icon color='#676767' />
                <Box>
                  <Text color={'gray.600'} fontSize={'sm'}>
                    {t('where')}
                  </Text>
                  <Text fontWeight={500}>{order?.to_address}</Text>
                </Box>
              </Flex>
            </>
          )}
        </Box>

        {order?.delivery_type === 'hall' && (
          <Flex gap={4} py={2} alignItems={'center'}>
            <CrmTableIcon color='#676767' />
            <Box>
              <Text color={'gray.600'} fontSize={'xs'}>
                {t('table_number')}
              </Text>
              <Text fontWeight={500} fontSize={20}>
                {order?.crm_table_number}
              </Text>
            </Box>
          </Flex>
        )}
        {order?.to_address_description && (
          <Flex flexDir={'column'} gap={3} bg={'background'} p={4} rounded={16}>
            <Text fontSize={18} fontWeight={500}>
              {t('comment_to_courier')}
            </Text>
            <Text fontSize={18} fontWeight={400}>
              {order?.description}
            </Text>
          </Flex>
        )}

        <Flex py={2} fontSize={18} justifyContent={'space-between'}>
          <Text>{t('payment_type')}</Text>
          <Text fontWeight={600} fontSize={20}>
            {order.payment_type_obj?.name[currentLocale]}
          </Text>
        </Flex>

        {order?.payment_type === 'cash' && change > 0 && (
          <HStack py={2} justify='space-between'>
            <Text fontSize={18}>{t('checkout.exchange')}</Text>
            <NumberToPrice
              textProps={{
                fontWeight: 600,
                fontSize: 20,
              }}
              spanProps={{
                fontWeight: 600,
                fontSize: 20,
              }}
              value={change}
            />
          </HStack>
        )}

        <HStack py={2} justify='space-between'>
          <Text fontSize={18}>{t('cost_of_goods')}</Text>
          <NumberToPrice
            textProps={{
              fontWeight: 600,
              fontSize: 20,
            }}
            spanProps={{
              fontWeight: 600,
              fontSize: 20,
            }}
            value={order?.steps?.[0]?.step_amount || 0}
          />
        </HStack>
        {order?.service_fee > 0 && (
          <Flex py={2} fontSize={18} justifyContent={'space-between'}>
            <Text>{t('checkout.service_fee')}</Text>
            <NumberToPrice
              textProps={{
                fontWeight: 600,
                fontSize: 20,
              }}
              spanProps={{
                fontWeight: 600,
                fontSize: 20,
              }}
              value={order.service_fee}
            />
          </Flex>
        )}
        {order?.delivery_type === 'delivery' && (
          <HStack py={2} justify='space-between'>
            <Stack gap={1}>
              <Text fontSize={18}>{t('cost_of_delivery')}</Text>
            </Stack>
            <NumberToPrice
              freeText={t('free_delivery')}
              isFreeTextVisible={order?.is_delivery_free}
              textProps={{
                fontWeight: 600,
                fontSize: 20,
              }}
              spanProps={{
                fontWeight: 600,
                fontSize: 20,
              }}
              value={order?.is_delivery_free ? 0 : order?.delivery_price}
            />
          </HStack>
        )}
        {order?.payment?.map((payment) => {
          return (
            payment?.payment_type === 'cashback' && (
              <HStack py={2} justify='space-between' key={payment?.id}>
                <Text fontSize={18}>
                  {payment?.payment_type === 'cashback'
                    ? t('cashback_title')
                    : payment?.payment_type_obj?.name?.[currentLocale]}
                </Text>
                <Flex gap={1}>
                  -
                  <NumberToPrice
                    value={payment?.paid_amount}
                    textProps={{ fontWeight: 600, fontSize: 20 }}
                    spanProps={{ fontWeight: 600, fontSize: 20 }}
                  />
                </Flex>
              </HStack>
            )
          );
        })}
        {order?.payment?.map((payment) => {
          return (
            payment?.payment_type === 'deposit' && (
              <HStack py={2} justify='space-between' key={payment?.id}>
                <Text fontSize={18}>
                  {payment?.payment_type === 'deposit'
                    ? t('deposit_title')
                    : payment?.payment_type_obj?.name?.[currentLocale]}
                </Text>
                <Flex gap={1}>
                  -
                  <NumberToPrice
                    value={payment?.paid_amount}
                    textProps={{ fontWeight: 600, fontSize: 20 }}
                    spanProps={{ fontWeight: 600, fontSize: 20 }}
                  />
                </Flex>
              </HStack>
            )
          );
        })}
        {order?.discounts?.map((discount) => (
          <HStack py={2} justify='space-between' key={discount.id}>
            <Text fontSize={18}>{discount?.name?.[currentLocale]}</Text>

            <NumberToPrice
              value={discount?.price || 0}
              textProps={{
                fontWeight: 600,
                fontSize: 20,
              }}
              spanProps={{
                fontWeight: 600,
                fontSize: 20,
              }}
            />
          </HStack>
        ))}
        <Separator borderColor={'background'} />
        <HStack py={2} pb={4} justify='space-between'>
          <Text fontWeight={500} fontSize={20}>
            {t('total')}
          </Text>
          <Box textAlign='right'>
            {(order?.discount_price !== 0 ||
              order?.steps?.[0]?.step_discount_price !== 0) && (
              <Text
                as='span'
                fontSize='sm'
                lineHeight={1}
                color='text.muted'
                fontWeight={400}
                textDecoration='line-through'
              >
                {numToPrice(
                  total -
                    order?.discount_price -
                    (order?.steps?.[0]?.step_discount_price || 0) +
                    (+order?.service_fee || 0),
                )}
              </Text>
            )}
            <NumberToPrice
              textProps={{
                fontWeight: 600,
                fontSize: 24,
                lineHeight: 1,
              }}
              spanProps={{
                fontWeight: 600,
                fontSize: 24,
                lineHeight: 1,
              }}
              value={total + (+order?.service_fee || 0)}
            />
          </Box>
        </HStack>
      </Stack>
    </Box>
  );
};

export default Desktop;
